/**
 * Video QoE — shared types, event names, and tuning constants.
 *
 * The emit side (controller, hook, player-hook signals) and the self-hosted
 * observability stack share this contract: every event name starts with
 * `video_` (the Alloy pipeline routes them to a single `action="video"`
 * stream), numeric attributes are emitted as stringified integers, and only
 * the attributes marked bounded below are safe to promote to Prometheus
 * labels.
 */

import type { RemoteVideoStatsSource } from '@module/helpers/play/watchFirstVideoFrame';

export type { RemoteVideoStatsSource };

// ─── Event names ───────────────────────────────────────────────────────────
export const VIDEO_SESSION_START_EVENT = 'video_session_start' as const;
export const VIDEO_FIRST_FRAME_EVENT = 'video_first_frame' as const;
export const VIDEO_ERROR_EVENT = 'video_error' as const;
export const VIDEO_START_FAILURE_EVENT = 'video_start_failure' as const;
export const VIDEO_PLAYBACK_FAILURE_EVENT = 'video_playback_failure' as const;
export const VIDEO_REBUFFER_EVENT = 'video_rebuffer' as const;
export const VIDEO_QUALITY_CHANGE_EVENT = 'video_quality_change' as const;
export const VIDEO_MODE_SWITCH_EVENT = 'video_mode_switch' as const;
export const VIDEO_HEARTBEAT_EVENT = 'video_heartbeat' as const;
export const VIDEO_SESSION_END_EVENT = 'video_session_end' as const;

// ─── Tuning ──────────────────────────────────────────────────────────────
/** Conviva-style heartbeat cadence. */
export const VIDEO_QOE_HEARTBEAT_MS = 20000;

/**
 * Per-view emission caps — a pathological session (reconnect storm, flapping
 * network) must not flood the collector. Past the cap the event folds into the
 * view totals only and `video_session_end.capped` becomes `true`.
 */
export const MAX_REBUFFER_EVENTS = 50;
export const MAX_ERROR_EVENTS = 30;
export const MAX_QUALITY_CHANGE_EVENTS = 20;

// ─── Domains / enums ───────────────────────────────────────────────────────
export type VideoMode = 'll' | 'rt';

/** Why an RT session gave up and handed off to LL. */
export type FailoverReason = 'deadline' | 'stage_fatal' | 'recovery_exhausted' | 'unsupported' | 'create_failed';

export type ViewEndReason = 'route_change' | 'pagehide' | 'fatal_error' | 'broadcast_ended';

/** Why the video element stalled — distinguishes real underruns from the two
 * cases where the element is swapped out from under playback. */
export type StallCause = 'underrun' | 'mode_switch' | 'player_reinit';

export type AutoplayBlockedSource = 'audio_blocked' | 'playback_blocked' | 'not_allowed';

/**
 * Signals published by the LL/RT player hooks at internal decision points that
 * an outside observer (element listeners, store subscriptions) cannot see:
 * error classification, retry accounting, and the reason an RT→LL failover
 * fired. The controller latches these and folds them into the emitted events.
 */
export type VideoQoeSignal =
  | { kind: 'll_error'; errorType: string; errorCode: number; message: string }
  | { kind: 'll_retry'; attempt: number }
  | { kind: 'll_fatal' }
  | { kind: 'rt_stage_error'; code: number; classification: ErrorClassification }
  | { kind: 'rt_retry'; attempt: number }
  | { kind: 'rt_failover'; reason: FailoverReason }
  | { kind: 'rt_fatal_no_ll' }
  | { kind: 'broadcast_ended' }
  | { kind: 'autoplay_blocked'; source: AutoplayBlockedSource }
  | { kind: 'rt_layer_selected'; reason: 'selected' | 'unavailable' };

export type ErrorClassification = 'recoverable' | 'fatal' | 'ignored';

/**
 * Minimal structural subset of the SDK's `RemoteVideoStats` the heartbeat
 * reads. Kept structural (not the SDK class) so tests need no SDK import and
 * `const enum NetworkQuality` never enters runtime code.
 */
export interface RemoteVideoStatsLike {
  bytesReceived?: number;
  packetsReceived?: number;
  packetsLost?: number;
  nackCount?: number;
  framesDecoded?: number;
  framesDropped?: number;
  freezeCount?: number;
  /** seconds, cumulative */
  totalFreezesDuration?: number;
  /** seconds, current */
  jitter?: number;
  /** seconds, cumulative */
  jitterBufferDelay?: number;
  jitterBufferEmittedCount?: number;
  /** 0 (DOWN) – 4 (EXCELLENT) */
  networkQuality?: number;
}

/** One heartbeat sample gathered by the hook and handed to the controller. */
export interface VideoQoeGauges {
  /** HTMLVideoElement.getVideoPlaybackQuality() — both modes. */
  droppedFrames?: number;
  totalFrames?: number;
  /** LL: player.getQuality().bitrate (bps). */
  bitrateBps?: number;
  /** LL: player.getLiveLatency() (seconds). */
  llLatencySec?: number;
  /** LL: player.getBufferDuration() (seconds). */
  llBufferSec?: number;
  /** RT: parsed RemoteVideoStats (cumulative counters). */
  rt?: RemoteVideoStatsLike;
}
